import React, { useCallback, useMemo, useEffect, useState } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Node,
  Edge,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { AutomationNodeType, AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { AutomationTriggerV2, AutomationNodeV2, FlowNodeData, NodeTemplate, AutomationEditorActions } from '../types';
import { generateNodeId } from '../utils';
import { FLOW_COLORS } from '../constants';
import { findNodeById } from '../utils/automationTreeUtils';
import { TriggerNodeV2, DelayNodeV2, ConditionNodeV2, ActionNodeV2, BranchNodeV2, PlaceholderNodeV2 } from '../NodeComponents';

// Simple End Node Component with handle for connection
const EndNodeV2: React.FC<{ data: any }> = () => (
  <div className="relative">
    <Handle
      type="target"
      position={Position.Top}
      isConnectable={false}
      className="w-3 h-3 bg-gray-400 border-2"
      style={{ borderColor: FLOW_COLORS.HANDLE.BORDER }}
    />
    <div className="bg-gray-100 text-gray-600 border border-gray-300 rounded-full px-4 py-2 text-sm font-medium">
      End
    </div>
  </div>
);
import { NodeOptionBar } from '../NodeOptionBar';

// Node types for ReactFlow - use V2 components
const nodeTypes = {
  TRIGGER: TriggerNodeV2,
  [AutomationNodeType.DELAY]: DelayNodeV2,
  [AutomationNodeType.CONDITION]: ConditionNodeV2,
  [AutomationNodeType.ACTION]: ActionNodeV2,
  BRANCH: BranchNodeV2,
  END: EndNodeV2, // Add End node type
  PLACEHOLDER: PlaceholderNodeV2, // Add Placeholder node type
};

interface FlowCanvasProps {
  trigger: AutomationTriggerV2;
  isEditing: boolean;
  selectedNodeId: string | null;
  onNodeClick: (nodeId: string) => void;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onAddChild: (parentId: string, template: NodeTemplate, isElseBranch?: boolean, isBranch?: boolean) => void;
  onDeleteNode: (nodeId: string) => void;
  actions: AutomationEditorActions;
  automationId?: string;
  templateId?: string;
  actionOptions?: any[];
}

export const FlowCanvas: React.FC<FlowCanvasProps> = ({
  trigger,
  isEditing,
  selectedNodeId,
  onNodeClick,
  onUpdateNode,
  onAddChild,
  onDeleteNode,
  actions,
  automationId,
  templateId,
  actionOptions,
}) => {
  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [showOptionBar, setShowOptionBar] = useState<{ nodeId: string; position: { x: number; y: number }; isElseBranch?: boolean } | null>(null);

  // Memoize callback functions to prevent infinite loops
  const memoizedOnNodeClick = useCallback(onNodeClick, [onNodeClick]);
  const memoizedOnUpdateNode = useCallback(onUpdateNode, [onUpdateNode]);
  const memoizedOnAddChild = useCallback(onAddChild, [onAddChild]);
  const memoizedOnDeleteNode = useCallback(onDeleteNode, [onDeleteNode]);

  // Convert automation tree to ReactFlow nodes and edges using V2 system
  const { flowNodes, flowEdges } = useMemo(() => {
    if (!trigger || !trigger.root) {
      return { flowNodes: [], flowEdges: [] };
    }
    const flowNodes: Node<FlowNodeData>[] = [];
    const flowEdges: Edge[] = [];

    // Layout configuration
    const NODE_WIDTH = 280;
    const HORIZONTAL_SPACING = 480;
    const BASE_VERTICAL_SPACING = 45; // Balanced spacing
    const CONDITION_BASE_HEIGHT = 20; // Base height for condition nodes
    const CONDITION_ITEM_HEIGHT = 32; // Height per condition item
    const CONDITION_PADDING = 24; // Padding for condition items
    const ADD_BUTTON_HEIGHT = 32; // Height for "Add Condition" button
    const DESCRIPTION_HEIGHT = 40; // Height for description text
    const TRIGGER_HEIGHT = 120; // Height for trigger nodes

    // Dynamic spacing calculation based on actual node content height
    const getVerticalSpacing = (node: AutomationNodeV2): number => {
      // Handle placeholder nodes specifically
      if (node.metadata?.isPlaceholder) {
        // Use expanded height for consistent spacing
        return 200 + BASE_VERTICAL_SPACING;
      }

      if (node.type === AutomationNodeType.CONDITION) {
        const conditions = node.metadata?.children || [];
        const singleCondition = node.metadata?.condition;
        const hasConditions = conditions.length > 0 || singleCondition;

        // Calculate actual content height
        let contentHeight = CONDITION_BASE_HEIGHT; // Base card height

        if (hasConditions) {
          // Add height for each condition badge
          const totalConditions = conditions.length + (singleCondition && conditions.length === 0 ? 1 : 0);
          contentHeight += totalConditions * CONDITION_ITEM_HEIGHT;
          contentHeight += (totalConditions - 1) * CONDITION_PADDING; // Spacing between conditions
        } else {
          // No conditions - add height for "No conditions set" badge
          contentHeight += CONDITION_ITEM_HEIGHT;
        }

        // Add height for "Add Condition" button if editing
        if (isEditing) {
          contentHeight += ADD_BUTTON_HEIGHT + CONDITION_PADDING;
        }

        // Add height for description text
        contentHeight += DESCRIPTION_HEIGHT;

        // Add height for True/False buttons at bottom
        if (isEditing) {
          contentHeight += 60; // Space for True/False buttons
        }

        // Calculate spacing needed to prevent overlap with compact padding for condition nodes
        const spacingNeeded = contentHeight + BASE_VERTICAL_SPACING + 15;

        return spacingNeeded;
      }

      // For ACTION nodes, calculate height based on content
      if (node.type === AutomationNodeType.ACTION) {
        const isConfigured = node.metadata && (
          (node.subType === AutomationActionNodeSubType.SEND_EMAIL && node.metadata.templateId && node.metadata.resourceId !== undefined && node.metadata.fromPreviousSender !== undefined) ||
          (node.subType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && node.metadata.boostId && node.metadata.resourceId !== undefined && node.metadata.fromPreviousSender !== undefined) ||
          (node.subType === AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST && node.metadata.resourceId !== undefined && node.metadata.fromPreviousSender !== undefined) ||
          (node.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && node.metadata.type && node.metadata.fieldValue)
        );

        let actionHeight = 100;

        if (isConfigured) {
          if (node.subType === AutomationActionNodeSubType.SEND_EMAIL) {
            // SendEmail has: template card + subject line + sender info + padding
            actionHeight += 120; // Increased to properly account for all content
          } else if (node.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA) {
            // UpdateMemberData has: action card + type/value line
            actionHeight += 70; // Increased from 50
          } else {
            // Other configured actions have smaller content
            actionHeight += 60;
          }
        } else {
          // Not configured - just the "Configure Action" button
          actionHeight += 40; // Increased from 30
        }

        return actionHeight + BASE_VERTICAL_SPACING;
      }

      // For DELAY nodes, calculate height based on configuration
      if (node.type === AutomationNodeType.DELAY) {
        const isConfigured = typeof node.metadata?.durationMs === 'number';
        let delayHeight = 80;

        if (isConfigured) {
          delayHeight += 35;
        } else {
          delayHeight += 30;
        }

        return delayHeight + BASE_VERTICAL_SPACING;
      }

      // For BRANCH nodes and other node types, use compact base spacing
      return BASE_VERTICAL_SPACING + 40;
    };

    // Track processed nodes to prevent cycles and bidirectional edges
    const processedNodes = new Set<string>();
    const activeNodes = new Set<string>(); // Track nodes in current path to detect cycles
    const createdEdges = new Set<string>(); // Track created edges to prevent duplicates

    // Helper function to safely add edges without duplicates
    const addEdge = (edge: Edge) => {
      const edgeKey = `${edge.source}->${edge.target}`;
      const reverseKey = `${edge.target}->${edge.source}`;

      // Check if this edge or its reverse already exists
      if (createdEdges.has(edgeKey)) {
        return;
      }

      if (createdEdges.has(reverseKey)) {
        return;
      }

      // Add the edge
      createdEdges.add(edgeKey);
      flowEdges.push(edge);
    };

    // Calculate positions using a hierarchical layout
    const calculateLayout = (node: AutomationNodeV2, x: number, y: number, depth: number): void => {
      // Prevent infinite loops and cycles
      if (activeNodes.has(node.id)) {
        return;
      }

      // Add to active path
      activeNodes.add(node.id);

      // Add extra spacing for the first few nodes to prevent overlap
      const extraSpacing = depth <= 3 ? 50 : 25;

      // Determine the ReactFlow node type - use PLACEHOLDER for placeholder nodes
      const reactFlowNodeType = node.metadata?.isPlaceholder ? 'PLACEHOLDER' : node.type;

      // Add the current node
      flowNodes.push({
        id: node.id,
        type: reactFlowNodeType,
        position: { x, y },
        data: {
          nodeId: node.id,
          nodeType: node.type,
          subType: node.subType,
          metadata: node.metadata,
          isEditing,
          isSelected: node.id === selectedNodeId,
          isRoot: node.id === trigger.root.id, // Mark as root if this is the root node of the automation tree
          onNodeClick: memoizedOnNodeClick,
          onUpdateNode: memoizedOnUpdateNode,
          onAddChild: memoizedOnAddChild,
          onReplacePlaceholder: actions.replacePlaceholder,
          onDeleteNode: memoizedOnDeleteNode,
          onShowOptionBar: (nodeId: string, position: { x: number; y: number }, isElseBranch?: boolean) => {
            setShowOptionBar({ nodeId, position, isElseBranch });
          },
          automationId,
          templateId,
          actionOptions,
        },
        draggable: false,
        selectable: true,
      });

      // Handle children and elseChildren for condition nodes
      if (node.type === AutomationNodeType.CONDITION) {
        const trueChildren = node.children || [];
        const falseChildren = node.elseChildren || [];

        // Position branches for better tree visualization when both True and False exist
        const hasBothBranches = trueChildren.length > 0 && falseChildren.length > 0;

        // If we have children, treat them as True branch
        if (trueChildren.length > 0) {
          const childY = y + getVerticalSpacing(node) + extraSpacing;

          trueChildren.forEach((child) => {
            // For proper tree layout: True branch goes significantly LEFT when both branches exist
            const childX = hasBothBranches ? x - HORIZONTAL_SPACING / 2 : x; // Symmetrical left offset for balanced tree
            calculateLayout(child, childX, childY, depth + 1);

            addEdge({
              id: `${node.id}-true-${child.id}`,
              source: node.id,
              target: child.id,
              sourceHandle: 'true',
              type: 'smoothstep',
              label: 'True',
              labelStyle: {
                fontSize: 12,
                fontWeight: 600,
                fill: FLOW_COLORS.LABEL.TRUE_TEXT,
              },
              labelBgStyle: {
                fill: FLOW_COLORS.LABEL_BG.TRUE,
                fillOpacity: 0.95,
                borderRadius: '6px',
                stroke: FLOW_COLORS.BORDER.TRUE,
                strokeWidth: 1,
              },
              markerEnd: {
                type: MarkerType.Arrow,
                color: FLOW_COLORS.MARKER.TRUE,
                width: 14,
                height: 14,
              },
              style: {
                stroke: FLOW_COLORS.EDGE.TRUE_BRANCH,
                strokeWidth: 1,
                strokeDasharray: '0',
              },
            });
          });
        }

        // Process false branch if exists - always place to the right
        if (falseChildren.length > 0) {
          const childY = y + getVerticalSpacing(node) + extraSpacing;

          falseChildren.forEach((child) => {
            // For proper tree layout: False branch goes RIGHT when both branches exist, centered when only False exists
            const childX = hasBothBranches ? x + HORIZONTAL_SPACING / 2 : x; // Center when only False, right when both exist
            calculateLayout(child, childX, childY, depth + 1);

            addEdge({
              id: `${node.id}-false-${child.id}`,
              source: node.id,
              target: child.id,
              sourceHandle: 'false',
              type: 'smoothstep',
              label: 'False',
              labelStyle: {
                fontSize: 12,
                fontWeight: 600,
                fill: FLOW_COLORS.LABEL.FALSE_TEXT,
              },
              labelBgStyle: {
                fill: FLOW_COLORS.LABEL_BG.FALSE,
                fillOpacity: 0.95,
                borderRadius: '6px',
                stroke: FLOW_COLORS.BORDER.FALSE,
                strokeWidth: 1,
              },
              markerEnd: {
                type: MarkerType.Arrow, // Outline arrow
                color: FLOW_COLORS.MARKER.FALSE,
                width: 14,
                height: 14,
              },
              style: {
                stroke: FLOW_COLORS.EDGE.FALSE_BRANCH,
                strokeWidth: 1,
                strokeDasharray: '0',
              },
            });
          });
        }
      } else if (node.children && node.children.length > 0) {
        if (node.children.length === 1) {
          // Single child - place directly below
          const childX = x;
          const childY = y + getVerticalSpacing(node) + extraSpacing;
          calculateLayout(node.children[0], childX, childY, depth + 1);

          // Add edge
          addEdge({
            id: `${node.id}-${node.children[0].id}`,
            source: node.id,
            target: node.children[0].id,
            type: 'smoothstep',
            markerEnd: {
              type: MarkerType.Arrow, // Outline arrow
              color: FLOW_COLORS.MARKER.DEFAULT,
              width: 14,
              height: 14,
            },
            style: {
              stroke: FLOW_COLORS.EDGE.DEFAULT,
              strokeWidth: 1, // Try thinner stroke
              strokeDasharray: '0',
            },
          });
        } else {
          // Multiple children - spread horizontally (branching)
          const totalWidth = (node.children.length - 1) * HORIZONTAL_SPACING;
          const startX = x - totalWidth / 2;
          const childY = y + getVerticalSpacing(node) + extraSpacing;

          node.children.forEach((child, index) => {
            const childX = startX + index * HORIZONTAL_SPACING;
            calculateLayout(child, childX, childY, depth + 1);

            // Add edge with branch labels for conditions
            const edgeLabel = node.type === AutomationNodeType.CONDITION
              ? (index === 0 ? 'True' : 'False')
              : undefined;

            addEdge({
              id: `${node.id}-${child.id}`,
              source: node.id,
              target: child.id,
              type: 'smoothstep',
              label: edgeLabel,
              labelStyle: {
                fontSize: 12,
                fontWeight: 500,
                fill: FLOW_COLORS.LABEL.DEFAULT_TEXT,
              },
              labelBgStyle: {
                fill: FLOW_COLORS.LABEL_BG.DEFAULT,
                fillOpacity: 0.9,
                borderRadius: '4px',
                stroke: FLOW_COLORS.BORDER.DEFAULT,
                strokeWidth: 1,
              },
              markerEnd: {
                type: MarkerType.Arrow, // Outline arrow
                color: FLOW_COLORS.MARKER.DEFAULT,
                width: 14,
                height: 14,
              },
              style: {
                stroke: FLOW_COLORS.EDGE.DEFAULT,
                strokeWidth: 1, // Try thinner stroke
              },
            });
          });
        }
      }

      // Remove from active path when done processing this node
      activeNodes.delete(node.id);
      processedNodes.add(node.id);
    };

        // Start with trigger node - center it horizontally
    flowNodes.push({
      id: trigger.id,
      type: 'TRIGGER', // Use TRIGGER type for proper node rendering
      position: { x: -NODE_WIDTH / 2, y: 0 },
      data: {
        nodeId: trigger.id,
        nodeType: 'TRIGGER' as any,
        metadata: { triggerType: trigger.type }, // Pass trigger type in correct format
        isEditing,
        isSelected: trigger.id === selectedNodeId,
        isRoot: true, // Trigger nodes are always root
        onNodeClick: memoizedOnNodeClick,
        onUpdateNode: memoizedOnUpdateNode,
        onAddChild: memoizedOnAddChild,
        onDeleteNode: memoizedOnDeleteNode,
        onShowOptionBar: (nodeId: string, position: { x: number; y: number }, isElseBranch?: boolean) => {
          setShowOptionBar({ nodeId, position, isElseBranch });
        },
      },
      draggable: false,
      selectable: true,
    });

    // Add edge from trigger to root node
    if (trigger.root) {
      addEdge({
        id: `trigger-${trigger.root.id}`,
        source: trigger.id,
        target: trigger.root.id,
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.Arrow, // Outline arrow
          color: FLOW_COLORS.MARKER.DEFAULT,
          width: 14,
          height: 14,
        },
        style: {
          stroke: FLOW_COLORS.EDGE.DEFAULT,
          strokeWidth: 1, // Try thinner stroke
          strokeDasharray: '0',
        },
      });

      // Layout the automation tree starting from root - center it horizontally
      // Use trigger height + base spacing for proper positioning
      calculateLayout(trigger.root, -NODE_WIDTH / 2, TRIGGER_HEIGHT + BASE_VERTICAL_SPACING, 1);
    }

    // Add "End" indicators for leaf nodes (nodes with no children)
    // NOTE: These are display-only nodes and will NOT be saved to the automation blueprint
    const addEndIndicators = () => {
      // Find all leaf nodes (nodes that have no children and no elseChildren)
      const leafNodes = flowNodes.filter((node) => {
        if (node.type === 'TRIGGER') return false; // Skip trigger nodes
        if (node.data?.metadata?.isEndNode) return false; // Skip existing end nodes

        // Check if this node has any children by looking at outgoing edges
        const hasOutgoingEdges = flowEdges.some((edge) => edge.source === node.id);
        return !hasOutgoingEdges;
      });

      leafNodes.forEach((leafNode) => {
        const endNodeId = `${leafNode.id}-end`;

        // Find the corresponding automation node to calculate dynamic spacing

        // Find the automation node corresponding to this leaf node
        const automationNode = leafNode.id === trigger.id ?
          { id: trigger.id, type: 'TRIGGER' as any, children: [], elseChildren: undefined, metadata: {}, variables: [] } :
          findNodeById(trigger.root, leafNode.id);

        // Calculate dynamic spacing using the same logic used for node positioning
        // This ensures End nodes are positioned consistently with how nodes are spaced in the tree
        const dynamicSpacing = automationNode ? getVerticalSpacing(automationNode) : BASE_VERTICAL_SPACING + 40;
        const endY = leafNode.position.y + (dynamicSpacing * 0.75) + 30; // 75% of spacing to prevent overlap while keeping arrows shorter

        // Add "End" node
        flowNodes.push({
          id: endNodeId,
          type: 'END', // Use custom END node type
          position: { x: leafNode.position.x + NODE_WIDTH / 2, y: endY }, // Center "End" exactly below the leaf node
          data: {
            nodeId: endNodeId,
            nodeType: 'END' as any,
            metadata: { isEndNode: true },
            isEditing: false,
            isSelected: false,
            isRoot: false,
          },
          draggable: false,
          selectable: false,
        } as any);

        // Add edge from leaf node to "End" - Clean straight line to prevent visual artifacts
        const isConditionWithoutChildren = leafNode.type === AutomationNodeType.CONDITION && automationNode &&
          (!automationNode.children || automationNode.children.length === 0) &&
          (!automationNode.elseChildren || automationNode.elseChildren.length === 0);

        addEdge({
          id: `${leafNode.id}-to-end`,
          source: leafNode.id,
          sourceHandle: isConditionWithoutChildren ? 'center' : undefined,
          target: endNodeId,
          type: 'straight',
          style: {
            stroke: FLOW_COLORS.EDGE.END_NODE,
            strokeWidth: 1,
            strokeDasharray: '0',
          },
          markerEnd: {
            type: MarkerType.Arrow, // Outline arrow for End nodes
            color: FLOW_COLORS.MARKER.END_NODE,
            width: 14,
            height: 14,
          },
        });
      });
    };

    addEndIndicators();

    return { flowNodes, flowEdges };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, isEditing, selectedNodeId]);

  // Update nodes and edges when trigger changes
  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  // Fit view when nodes change with better padding for dynamic content
  useEffect(() => {
    if (flowNodes.length > 0) {
      setTimeout(() => {
        fitView({
          padding: 0.15, // Adjusted padding for better view
          includeHiddenNodes: false,
          duration: 800, // Smooth animation
        });
      }, 150); // Slightly longer delay to ensure all nodes are rendered
    }
  }, [flowNodes.length, fitView]);

  // Handle node clicks
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.stopPropagation();
    onNodeClick(node.id);
  }, [onNodeClick]);

  // Handle canvas click (deselect)
  const handlePaneClick = useCallback(() => {
    onNodeClick(null); // Always deselect when clicking on canvas
    setShowOptionBar(null);
  }, [onNodeClick]);

  // Handle drag over
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  // Handle drop
  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    try {
      const templateData = event.dataTransfer.getData('application/json');
      const template: NodeTemplate = JSON.parse(templateData);

      // For now, add to the root node - you could enhance this to detect the target node
      const newNode: AutomationNodeV2 = {
        id: generateNodeId(),
        type: template.type,
        subType: template.subType,
        children: [],
        metadata: { ...template.defaultMetadata },
        variables: [],
      };

      // Add to the first available parent (root in this case)
      actions.addNode(trigger.root.id, newNode);
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }, [actions, trigger.root?.id]);

  return (
    <div
      className="h-full w-full relative"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={isEditing}
        selectNodesOnDrag={false}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        className={FLOW_COLORS.BACKGROUND.CANVAS_CLASS}
        fitView
        minZoom={0.3}
        maxZoom={1.8}
      >
        <Background
          color={FLOW_COLORS.BACKGROUND.GRID}
          gap={16}
          size={1}
          style={{ opacity: isEditing ? 0.5 : 0.2 }}
        />
        <Controls
          showInteractive={false}
          className="bg-white border border-grey-2 rounded-lg shadow-sm"
        />
      </ReactFlow>

      {/* Node Option Bar */}
      {showOptionBar && (
        <NodeOptionBar
          parentNodeId={showOptionBar.nodeId}
          onAddNode={onAddChild}
          onClose={() => setShowOptionBar(null)}
          position={showOptionBar.position}
          isElseBranch={showOptionBar.isElseBranch}
        />
      )}
    </div>
  );
};
